import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { DeleteWarningIcon, OnlineSuccess, OfflineIcon } from '../ui/icons';
import { usePendingDeletionsModal } from './usePendingDeletionsModal';
import { IPendingDeletion } from '../../common/types/devicesTypes';
import './pendingDeletionsModal.scss';

const formatDate = (value?: string): string =>
  value ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

const getStateLabel = (device: IPendingDeletion): string => {
  if (device.deletionStatus === 'deletion_failed') {
    return device.lastDeletionFailureReason
      ? `Failed - ${device.lastDeletionFailureReason.replace(/_/g, ' ')}`
      : 'Failed';
  }
  return 'Waiting for device to reconnect';
};

const PendingDeletionsModal = () => {
  const {
    showModal,
    handleOpen,
    handleClose,
    pendingDevices,
    isLoading,
    pendingCount,
    deviceToRemove,
    askToRemove,
    cancelRemove,
    confirmRemove,
  } = usePendingDeletionsModal();

  return (
    <>
      <Button className="pending-deletions-trigger" onClick={handleOpen}>
        Pending Deletions{' '}
        <span className="notification">
          {isLoading ? <span className="mini-loader"></span> : pendingCount}
        </span>
      </Button>

      <Modal
        show={showModal && !deviceToRemove}
        onHide={handleClose}
        className="modal-container pending-deletions-modal"
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Pending Deletions</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!isLoading && pendingDevices.length === 0 && (
            <p className="pending-deletions-empty">No devices are waiting to be deleted.</p>
          )}

          {!isLoading && pendingDevices.length > 0 && (
            <div className="pending-deletions-table-wrap">
              <table className="pending-deletions-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Organization</th>
                    <th>Status</th>
                    <th>State</th>
                    <th>Deleted by</th>
                    <th>Deleted on</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {pendingDevices.map((device) => (
                    <tr key={device.deviceId}>
                      <td>
                        <div className="device-name">{device.displayName || device.deviceId}</div>
                        <div className="device-id">{device.deviceId}</div>
                      </td>
                      <td>{device.organizationName || '-'}</td>
                      <td>
                        <div className="device-connection">
                          {device.deviceStatus === 'connected' ? <OnlineSuccess /> : <OfflineIcon />}
                          <span>{device.deviceStatus === 'connected' ? 'Online' : 'Offline'}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={
                            device.deletionStatus === 'deletion_failed'
                              ? 'state-pill state-failed'
                              : 'state-pill state-waiting'
                          }
                        >
                          {getStateLabel(device)}
                        </span>
                      </td>
                      <td>{device.deletionRequestedBy || '-'}</td>
                      <td>{formatDate(device.deletionRequestedAt)}</td>
                      <td>
                        <Button className="btn-force-remove" onClick={() => askToRemove(device)}>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button className="btn-cancel" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deviceToRemove} onHide={cancelRemove} className="modal-container" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="delete-heading">
              <DeleteWarningIcon />
              <h1>Delete</h1>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This action will remove{' '}
            {deviceToRemove?.displayName || deviceToRemove?.deviceId} from the
            organisation permanently. In case the device comes online, it will
            need to be reflashed for setting it up again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={cancelRemove}>
            Cancel
          </Button>
          <Button className="btn-delete" onClick={confirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PendingDeletionsModal;
